import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { useMemo } from 'react';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { COMMUNICATION_LANGUAGES, CommunicationLocaleSchema } from 'types/locale';
import { z } from 'zod/v4';

const URL = '/communication_locales';

const CommunicationLocaleUsageSchema = z.object({
  debtors: z.number(),
  contactPeople: z.number(),
  templateBodies: z.number(),
});

const AvailableCommunicationLocaleSchema = z.object({
  value: CommunicationLocaleSchema,
  enabled: z.boolean(),
  isDefault: z.boolean(),
  canDisable: z.boolean(),
  usage: CommunicationLocaleUsageSchema,
});

export type AvailableCommunicationLocale = z.infer<typeof AvailableCommunicationLocaleSchema>;

export const CommunicationLocalesSummarySchema = z.object({
  enabled: z.array(CommunicationLocaleSchema),
  defaultLocale: CommunicationLocaleSchema,
});

const CommunicationLocalesSchema = CommunicationLocalesSummarySchema.extend({
  available: z.array(AvailableCommunicationLocaleSchema),
});

export type CommunicationLocales = z.infer<typeof CommunicationLocalesSchema>;

// `defaultLocale` has no meaningful value before the company answers; it is never read while the
// list is still empty.
const PLACEHOLDER: CommunicationLocales = { enabled: [], defaultLocale: 'fr', available: [] };

async function loadCommunicationLocalesQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = CommunicationLocalesSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'communicationLocales');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadCommunicationLocales(options: QueryOptions<CommunicationLocales> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<CommunicationLocales, ApiError>({
    queryKey: ['communicationLocales'],
    queryFn: () => loadCommunicationLocalesQueryFn(axiosInstance),
    placeholderData: previousData => (isDefined(previousData) ? previousData : PLACEHOLDER),
    ...options,
  });

  // The enabled set with its labels, for the language pickers. Empty until the request lands, so
  // none of them offers a language the company has switched off.
  const communicationLanguages = useMemo(() => {
    const enabled = queryResult.data?.enabled ?? [];

    return COMMUNICATION_LANGUAGES.filter(language => enabled.includes(language.value));
  }, [queryResult.data]);

  return {
    ...addResourceNameToQueryResult<
      CommunicationLocales,
      ApiError,
      'communicationLocales',
      'PreventUndefined'
    >('communicationLocales', queryResult, PLACEHOLDER),
    communicationLanguages,
  };
}
